import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@uidotdev/usehooks";
import { useQuery } from "@tanstack/react-query";
import useApiHelper from "../../hooks/useApiHelper";
import { DefaultResponseWithData } from "../../models/system";
import LoadingWheel from "../shared/LoadingWheel";
import { UKVDResponse } from "../../models/UKVDResponse";
import apiConfig from "../../utils/apiConfig";


interface ModelComboboxProps {
  onModelSelect: (model: UKVDResponse) => void;
  currentValue?: string;
  label?: string;
  make?: string;
}

export function ModelCombobox({ onModelSelect, currentValue, label, make }: ModelComboboxProps) {
  const { t } = useTranslation("stock");
  const { get } = useApiHelper(apiConfig("ModelCombobox"));
  const [selectedModel, setSelectedModel] = useState<UKVDResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const previousMakeRef = useRef<string | undefined>(make);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["model", "lookup", make],
    queryFn: () =>
      get<DefaultResponseWithData<UKVDResponse[]>>(
        `/utilities/lookup/makes${make ? `?make=${encodeURIComponent(make)}` : ''}` // Assuming the API supports filtering by make
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    keepPreviousData: true,
    enabled: !!make, // Only fetch if make is provided
  });

  const models = useMemo(() => {
    const allModels = data?.data ?? [];

    if (!debouncedSearch) return allModels;

    return allModels.filter(model =>
      model.range?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [data, debouncedSearch]);

  // Handle both make changes and currentValue updates
  useEffect(() => {
    // First, handle make changes
    if (previousMakeRef.current !== make) {
      setSelectedModel(null);
      setSearchTerm("");
      previousMakeRef.current = make;
      
      // If we have a currentValue and data is available, try to set it after clearing
      if (currentValue && currentValue !== "" && data?.data && !isLoading) {
        const searchValue = currentValue.toLowerCase();
        const foundModel = data.data.find(model => model.range?.toLowerCase() === searchValue);
        if (foundModel) {
          setSelectedModel(foundModel);
          setSearchTerm(foundModel.range || "");
        } else {
          // Do not force-clear parent here; parent clears on make select
        }
      } else {
        // Do not force-clear parent here
      }
      return; // Exit early after handling make change
    }
    
    // Then handle currentValue updates (only if make hasn't changed)
    if (isLoading || currentValue === undefined) return;

    // Clear the field if empty
    if (currentValue === "") {
      setSelectedModel(null);
      setSearchTerm("");
      return;
    }

    const searchValue = currentValue.toLowerCase();
    const foundModel = data?.data.find(model => model.range?.toLowerCase() === searchValue);

    if (foundModel && foundModel !== selectedModel) {
      setSelectedModel(foundModel);
      setSearchTerm(foundModel.range || "");
    } else if (!foundModel && currentValue !== "") {
      // If the model is not found and we have a value, reset the selection
      setSelectedModel(null);
      setSearchTerm(currentValue);
    }

  }, [make, currentValue, data, isLoading, selectedModel]);


  return (
    <div>
      {label &&
        <label>
          <span className="text-sm font-medium text-gray-500">{label}</span>
        </label>
      }
      <Combobox
        as="div"
        by="range"
        value={selectedModel ?? null}
        onChange={(model: UKVDResponse) => {
          setSelectedModel(model);
          onModelSelect(model);
        }}
        key={make} // Force re-render when make changes
      >
        <div className="relative mt-1">
          <ComboboxInput
            className="w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-400"
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
            displayValue={(model: UKVDResponse) => (model?.range || searchTerm)}
            placeholder={t("Search models...")}
            autoComplete="off"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            {make && (isLoading || isFetching) ? (
              <div className="flex items-center justify-center h-5 w-5">
                <LoadingWheel />
              </div>
            ) : (
              <ChevronUpDownIcon
                className="size-5 text-gray-400"
                aria-hidden="true"
              />
            )}
          </ComboboxButton>

          <ComboboxOptions
            className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-b-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {models && models.length > 0 ? (
              models.slice(0, 100).map((model) => (
                <ComboboxOption
                  key={model.range}
                  value={model}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                          }`}
                      >
                        {model.range}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                            }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            ) : (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                {t("Nothing found.")}
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox >
    </div >
  );
}
